import React, { useEffect, useState, useCallback } from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Radio, Rate, Typography, notification } from 'antd';
import { connect } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import get from 'lodash/get';
//
import { QACriteriaService, QACriteriaScoreService } from 'services';
import styles from './FeedbackForm.less';

const { Text } = Typography;

const FeedbackFormC = (props) => {
  const { instructorId, lessonId, form, setSubmitting, user, setIsSubmitted, isSubmitDraft } =
    props;

  const { getFieldDecorator, validateFields, resetFields } = form;

  const [criteria, setCriteria] = useState([]);
  const [scores, setScores] = useState(null);
  const [validateStatus, setValidateStatus] = useState({});

  useEffect(() => {
    fetchCriteria();
  }, []);

  useEffect(() => {
    resetFields();
    fetchCriteriaScores(lessonId);
  }, [fetchCriteriaScores, lessonId, resetFields]);

  const fetchCriteriaScores = useCallback(
    async (lessonId) => {
      if (!lessonId) return;
      try {
        const {
          data: [data],
          total,
        } = await QACriteriaScoreService.find({
          query: {
            instructorId,
            lessonId,
            $limit: 1,
          },
        });

        if (data && data.isDraft) {
          setIsSubmitted(false);
        } else {
          resetFields();
          setIsSubmitted(total > 0);
        }

        setScores(data);
      } catch (err) {
        console.error(err);
        notification['error']({
          message: 'Load Error!',
          description: 'An error has occured while loading the QA feedback.',
        });

        // In case we cannot fetch, we reset the fields anyways so the teachers
        // won't see any false info on the modal, reducing confusion.
        resetFields();
      }
    },
    [instructorId, resetFields, setIsSubmitted]
  );

  const fetchCriteria = async () => {
    const res = await QACriteriaService.find({
      query: {
        $sort: { index: 1 },
        isActive: true,
      },
    });

    setCriteria(res.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const getNotificationContent = (isDraft) => {
      if (isDraft) {
        return {
          message: 'Successfully Saved!',
          description: 'Quality Assurance form has been saved.',
        };
      }

      resetFields();
      setIsSubmitted(true);
      return {
        message: 'Successfully Submitted!',
        description: 'Quality Assurance form has been submitted successfully.',
      };
    };

    validateFields(async (err, values) => {
      if (err && !isSubmitDraft) {
        let newValidateStatus = {};
        for (let e in err) {
          const { errors } = err[e];
          const { message, field } = errors[0];
          const scoreId = field.split('-')[1];
          newValidateStatus[scoreId] = message;
        }

        setValidateStatus(newValidateStatus);
      } else {
        setValidateStatus({});
        const criteriaScores = [];
        criteria.forEach((criterion) => {
          criteriaScores.push({
            QACriteriaId: criterion._id,
          });
        });

        for (let val in values) {
          if (values[val] === undefined) continue;
          const [type, critId] = val.split('-');
          for (let i = 0; i < criteriaScores.length; i++) {
            if (criteriaScores[i].QACriteriaId === critId) {
              criteriaScores[i][type] = values[val];
              break;
            }
          }
        }

        const data = {
          instructorId,
          reviewerId: user._id,
          lessonId,
          criteriaScores,
          isDraft: isSubmitDraft,
        };

        try {
          setSubmitting(true);
          let updatedData;
          if (scores && !!scores._id) {
            updatedData = await QACriteriaScoreService.patch(scores._id, data);
          } else {
            updatedData = await QACriteriaScoreService.create(data);
          }

          notification['success'](getNotificationContent(isSubmitDraft));

          if (updatedData) setScores(updatedData);
          setSubmitting(false);
        } catch (e) {
          console.error(e);
          notification['error']({
            message: 'Submit Error!',
            description: 'An error has occured during the submission.',
          });
        }
      }
    });
  };

  const getInitialComment = (index) => {
    const comment = get(scores, `criteriaScores[${index}].comment`);
    return comment || null;
  };

  const getInitialScore = (index) => {
    const score = get(scores, `criteriaScores[${index}].score`);
    return score || null;
  };

  return scores && !scores.isDraft ? (
    <div>
      <p>
        Feedback was submitted for this lesson on {moment(scores.createdAt).format('MMMM Do YYYY')}.
      </p>
      <Form layout="vertical" id="feedbackScoresForm">
        {criteria.map((criterion, index) => {
          const isStar = criterion.scoreType === '5-star';
          const { criteriaScores } = scores;
          return (
            criterion._id === criteriaScores[index].QACriteriaId && (
              <Form.Item key={criterion._id} label={criterion.title}>
                <div className={styles.criterionWrapper}>
                  <div className={styles.criterionDetails}>
                    <div className={styles.criterionDescription}>
                      <Text type="secondary">{criterion.description}</Text>
                    </div>
                    <p>{criteriaScores[index].comment}</p>
                  </div>
                  <div>
                    {getFieldDecorator(`score-${criterion._id}`, {
                      initialValue: criteriaScores[index].score,
                    })(
                      isStar ? (
                        <Rate className={styles.scoreInput} disabled />
                      ) : (
                        <Radio.Group className={styles.scoreInput} disabled>
                          <Radio value="pass">Pass</Radio>
                          <Radio value="fail">Fail</Radio>
                        </Radio.Group>
                      )
                    )}
                  </div>
                </div>
              </Form.Item>
            )
          );
        })}
      </Form>
    </div>
  ) : (
    <Form layout="vertical" id="feedbackForm" onSubmit={handleSubmit}>
      {criteria.map((criterion, index) => {
        const isStar = criterion.scoreType === '5-star';
        return (
          <Form.Item
            key={criterion._id}
            label={criterion.title}
            help={<div className={styles.validationMessage}>{validateStatus[criterion._id]}</div>}
            required
          >
            <div className={styles.criterionWrapper}>
              <div className={styles.criterionDetails}>
                <div className={styles.criterionDescription}>
                  <Text type="secondary">{criterion.description}</Text>
                </div>
                {getFieldDecorator(`comment-${criterion._id}`, {
                  initialValue: getInitialComment(index),
                })(<Input.TextArea placeholder="Write a comment (optional)" autoSize />)}
              </div>
              <div>
                {getFieldDecorator(`score-${criterion._id}`, {
                  rules: [
                    {
                      type: isStar ? 'integer' : 'string',
                      required: true,
                      message: isStar ? 'Please rate!' : 'Please select!',
                    },
                  ],
                  initialValue: isStar ? getInitialScore(index) : 'pass',
                })(
                  isStar ? (
                    <Rate className={styles.scoreInput} />
                  ) : (
                    <Radio.Group className={styles.scoreInput}>
                      <Radio value="pass">Pass</Radio>
                      <Radio value="fail">Fail</Radio>
                    </Radio.Group>
                  )
                )}
              </div>
            </div>
          </Form.Item>
        );
      })}
    </Form>
  );
};

FeedbackFormC.propTypes = {
  instructorId: PropTypes.string.isRequired,
  lessonId: PropTypes.string.isRequired,
  setSubmitting: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  setIsSubmitted: PropTypes.func.isRequired,
  isSubmitDraft: PropTypes.bool.isRequired,
  form: PropTypes.object.isRequired,
};

const FeedbackFormF = Form.create()(FeedbackFormC);

const mapState = ({ auth }) => ({
  user: auth.user,
});

export const FeedbackForm = connect(mapState)(FeedbackFormF);
